'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { DATA_HEADER_NAME } from '@portals/hc-rich-editor';
import {
  ACTIVE_SECTION_OFFSET,
  HEADING_SELECTOR,
} from '@/_components/article/_components/side-navigation/_hooks/contants';
import { useHeaderHeight } from './use-header-height';

type THeading = {
  element: HTMLElement;
  text: string;
  indent: number;
};

type TProps = {
  materialId: string;
  title: string;
};

export const useArticleNavigation = ({ materialId, title }: TProps) => {
  const headerHeight = useHeaderHeight();
  const railRef = useRef<HTMLElement>(null);
  const [headings, setHeadings] = useState<Array<THeading>>([]);
  const [activeHeading, setActiveHeading] = useState<HTMLElement | null>(null);

  const selectedPosition = useRef<number | null>(null);
  const selectHeading = useCallback((element?: HTMLElement) => {
    selectedPosition.current = window.scrollY;
    setActiveHeading(element ?? null);
  }, []);

  useEffect(() => {
    selectedPosition.current = null;
    const article = railRef.current?.parentElement;
    if (!article || headerHeight === null) {
      return;
    }

    // Читаем отрисованные заголовки, чтобы текст, переменные и старые якорные
    // идентификаторы совпадали с результатом парсера
    const elements = Array.from(
      article.querySelectorAll<HTMLElement>(
        `[data-name="${DATA_HEADER_NAME}"]`,
      ),
    );
    const articleHeadings = elements.map((element) => ({
      element,
      text: element.firstElementChild?.textContent || '',
      level: Number(
        element.querySelector(HEADING_SELECTOR)?.tagName.slice(1) || 1,
      ),
    }));
    const minLevel = Math.min(...articleHeadings.map(({ level }) => level));
    setHeadings(
      articleHeadings.map((heading) => ({
        element: heading.element,
        text: heading.text,
        indent: heading.level - minLevel,
      })),
    );
    // Выбираем последний заголовок, пересёкший линию переключения.
    // Пока ни один не пересёк её, активным остаётся название статьи.
    // Известные граничные случаи:
    // 1. В конце страницы последний заголовок может не достичь линии переключения.
    //    Тогда при обычной прокрутке выделенным останется предыдущий раздел.
    // 2. После клика изменение ширины окна может перераспределить текст без изменения
    //    scrollY. Фиксация выбранного пункта заблокирует пересчёт до следующего скролла.
    const updateActiveSection = () => {
      // Не отменяем выбор кликом из-за отступа якоря или ограничения прокрутки внизу.
      // После изменения позиции страницы снова определяем раздел по геометрии.
      if (selectedPosition.current === window.scrollY) {
        return;
      }
      selectedPosition.current = null;
      let active: HTMLElement | null = null;
      for (const element of elements) {
        if (element.getBoundingClientRect().top > ACTIVE_SECTION_OFFSET) {
          break;
        }
        active = element;
      }
      setActiveHeading(active);
    };

    let frameId: number | null = null;
    const scheduleUpdate = () => {
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateActiveSection();
      });
    };
    const observer = new IntersectionObserver(updateActiveSection, {
      rootMargin: `-${headerHeight}px 0px 0px 0px`,
    });
    elements.forEach((element) => observer.observe(element));
    updateActiveSection();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [headerHeight, materialId, title]);

  return { railRef, headings, activeHeading, selectHeading };
};
